/**
 * "Show more" pagination for collection grids. Each click requests the next page of the section
 * and appends its product cards to the grid that is already on screen, so a six page collection
 * is reached in five clicks instead of five page loads.
 *
 * Only product cards are carried across. The collection hero tile and the featured product card
 * blocks are positioned relative to the start of a page, so every page's markup contains them
 * and copying them over would duplicate them further down the grid.
 */
if (!customElements.get('collection-show-more')) {
  customElements.define(
    'collection-show-more',
    class CollectionShowMore extends HTMLElement {
      connectedCallback() {
        if (this.button) return;

        this.button = this.querySelector('.collection-show-more__button');
        this.status = this.querySelector('.collection-show-more__status');
        if (this.button) this.button.addEventListener('click', this.loadNextPage.bind(this));
      }

      get grid() {
        return (this.closest('.collection') || document).querySelector('collection-grid');
      }

      /* Requesting the page through the Section Rendering API keeps the payload to this section
         instead of a whole collection page. The URL already carries any active filters and sort. */
      get nextPageUrl() {
        const url = new URL(this.dataset.url, window.location.origin);
        if (this.dataset.sectionId) url.searchParams.set('section_id', this.dataset.sectionId);
        return url.toString();
      }

      async loadNextPage() {
        if (this.loading || !this.dataset.url) return;
        this.setLoading(true);

        try {
          const response = await fetch(this.nextPageUrl);
          if (!response.ok) throw new Error(`Show more request failed with status ${response.status}`);

          const nextPage = new DOMParser().parseFromString(await response.text(), 'text/html');
          const added = this.appendProducts(nextPage);
          this.announce(`${added} more product${added === 1 ? '' : 's'} loaded.`);
          this.setNextPage(nextPage);
        } catch (error) {
          console.error(error);
          this.announce('Sorry, more products could not be loaded. Please try again.', true);
        } finally {
          this.setLoading(false);
        }
      }

      appendProducts(nextPage) {
        const grid = this.grid;
        const items = nextPage.querySelectorAll('collection-grid .grid__item--product');
        if (!grid || items.length === 0) return 0;

        const fragment = document.createDocumentFragment();

        items.forEach((item) => {
          /* The reveal-on-scroll observer only watches elements that were in the document at
             load, so anything appended afterwards would stay stuck at its hidden start state. */
          item.classList.add('scroll-trigger--cancel');
          item.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
          fragment.appendChild(item);
        });

        grid.appendChild(fragment);
        this.bindShadeSwatches(grid);

        return items.length;
      }

      /* Shade pickers are wired up per card as the page loads, so appended cards need the same. */
      bindShadeSwatches(grid) {
        grid.querySelectorAll('.product-card-wrapper').forEach((card) => {
          const trigger = card.querySelector('.select-shade.btn');
          const options = card.querySelector('.product__card-options');
          if (!trigger || !options || trigger.dataset.shadeBound) return;

          trigger.dataset.shadeBound = 'true';
          trigger.addEventListener('click', () => {
            options.classList.add('show');
            trigger.style.opacity = '0';
          });
        });
      }

      setNextPage(nextPage) {
        const next = nextPage.querySelector('collection-show-more');

        if (next && next.dataset.url) {
          this.dataset.url = next.dataset.url;
          return;
        }

        delete this.dataset.url;
        this.button.hidden = true;
        this.classList.add('collection-show-more--complete');
      }

      setLoading(loading) {
        this.loading = loading;
        this.classList.toggle('collection-show-more--loading', loading);
        this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
        this.button.disabled = loading;
      }

      announce(message, isError = false) {
        if (!this.status) return;

        this.status.textContent = message;
        this.status.classList.toggle('collection-show-more__status--error', isError);
      }
    }
  );
}
